import { logger } from '@/services/LoggingService';
import { VezgoStorageKeys } from './storage-keys';
import { vezgoStorage } from './storage-keys';
import { ensureVezgoAccountId, isValidVezgoAccountId } from './type-helpers';

// Define timeout for callback processing flags (5 minutes)
const CALLBACK_FLAG_TIMEOUT_MS = 5 * 60 * 1000;

export class CallbackProcessor {
  /**
   * Clean up stale callback processing flags
   */
  static cleanupStaleFlags(): void {
    const processed = vezgoStorage.getCallbackProcessed() === 'true';
    if (processed) {
      const processTimeStr = vezgoStorage.getCallbackTime();
      if (processTimeStr) {
        const elapsed = Date.now() - parseInt(processTimeStr, 10);
        if (elapsed > CALLBACK_FLAG_TIMEOUT_MS) {
          vezgoStorage.clearCallbackProcessed();
          vezgoStorage.clearCallbackTime();
          vezgoStorage.clearCallbackProvider();
          vezgoStorage.clearCallbackConnectionId();
          logger.info('CallbackProcessor', 'Removed stale callback flags');
        }
      }
    }
  }

  /**
   * Check if a callback was recently processed
   */
  static isCallbackRecentlyProcessed(): boolean {
    const processed = vezgoStorage.getCallbackProcessed() === 'true';
    if (!processed) return false;
    
    const processTimeStr = vezgoStorage.getCallbackTime();
    if (!processTimeStr) return false;
    
    const elapsed = Date.now() - parseInt(processTimeStr, 10);
    return elapsed < CALLBACK_FLAG_TIMEOUT_MS;
  }

  /**
   * Mark a callback as processed, storing metadata
   * @param accountId Can be any type, will be converted to string
   */
  static markCallbackProcessed(accountId: unknown): void {
    try {
      // CRITICAL FIX: Force explicit string conversion at the boundary with proper typing
      // This ensures TypeScript understands we're handling any potential type correctly
      const idAsString: string = String(accountId);
      
      vezgoStorage.setCallbackProcessed('true');
      // CRITICAL FIX: Ensure Date.now() is explicitly converted to string to avoid type confusion
      const timestamp: string = Date.now().toString();
      vezgoStorage.setCallbackTime(timestamp);
      vezgoStorage.setCallbackProvider(idAsString);
      
      logger.info('CallbackProcessor', 'Marked callback processed', {
        accountId: idAsString,
        time: new Date().toISOString()
      });
    } catch (err) {
      logger.error('CallbackProcessor', 'Failed to mark callback as processed', {
        error: err instanceof Error ? err.message : String(err),
        accountId: String(accountId)
      });
      // We're not throwing an error as this is non-critical functionality
      // This makes the callback processor more robust to different input types
    }
  }

  /**
   * Get detailed information about the last processed callback
   */
  static getCallbackDetails() {
    return {
      processed: vezgoStorage.getCallbackProcessed() === 'true',
      time: vezgoStorage.getCallbackTime(),
      accountId: vezgoStorage.getCallbackProvider()
    };
  }

  /**
   * Reset all callback processing state
   */
  static resetCallbackProcessingState(): void {
    vezgoStorage.clearCallbackProcessed();
    vezgoStorage.clearCallbackTime();
    vezgoStorage.clearCallbackProvider();
    vezgoStorage.clearCallbackConnectionId();
    logger.info('CallbackProcessor', 'Reset all callback flags');
  }
}
